"use client";

import { ISection } from "@faire/web-api/indigofair/data/ISection";
import { IViewLoggingData } from "@faire/web-api/indigofair/data/IViewLoggingData";
import React, { useMemo } from "react";

import {
  SDUISectionInstanceRendererProps,
  SectionLoggingEventParams,
} from "@faire/retailer/components/SDUI/__internal__/types";
import {
  ComponentRenderMap,
  SDUIUPLTProps,
  SectionRenderMap,
} from "@faire/retailer/components/SDUI/types";

interface SDUISectionRendererProps {
  section: ISection;
  componentRenderers: ComponentRenderMap;
  sectionRenderers: SectionRenderMap;
  viewLoggingData?: IViewLoggingData;
  loadTimingRef?: React.RefCallback<HTMLElement>;
  // performance related props. Existence to be re-evaluated after SD UI supports lazy loading
  shouldRenderBelowTheFold?: boolean;
  UPLTprops?: SDUIUPLTProps;
}

export const SDUISectionRenderer: React.FC<SDUISectionRendererProps> = ({
  section,
  componentRenderers,
  sectionRenderers,
  viewLoggingData,
  loadTimingRef,
  UPLTprops,
  shouldRenderBelowTheFold,
}: SDUISectionRendererProps) => {
  const sectionLoggingData = useMemo(
    () =>
      SectionLoggingEventParams.build({
        ...viewLoggingData,
        ...section.logging_data,
      }),
    [viewLoggingData, section.logging_data]
  );

  const SectionRenderer = getSectionRenderer(section, sectionRenderers);

  if (!SectionRenderer) {
    return null;
  }

  return (
    <SectionRenderer
      section={section}
      componentRenderers={componentRenderers}
      sectionLoggingData={sectionLoggingData}
      loadTimingRef={loadTimingRef}
      shouldRenderBelowTheFold={shouldRenderBelowTheFold}
      UPLTprops={UPLTprops}
    />
  );
};

const getSectionRenderer = (
  section: ISection,
  sectionRenderers: SectionRenderMap
): React.FC<SDUISectionInstanceRendererProps> | undefined => {
  if (section.column_section) {
    return sectionRenderers["column_section"];
  } else if (section.filterable_paginated_section) {
    return sectionRenderers["filterable_paginated_section"];
  } else if (section.rotating_section) {
    return sectionRenderers["rotating_section"];
  }
  return undefined;
};
